'use client'
import { useState } from 'react'

import StarRating from '@/components/starraing-new'
import Image from 'next/image'

type ReviewCardProps = {
  name: string
  date: string
  rating: number
  review: string
  avatarUrl: string
  source: string
}

export function ReviewCard({
  name,
  date,
  rating,
  review,
  avatarUrl,
  source,
}: ReviewCardProps) {
  function shortenName(fullName: string) {
    const parts = fullName.trim().split(' ')
    if (parts?.length > 1) {
      const firstName = parts[0]
      const lastInitial = parts[1][0] // take only first character
      return `${firstName} ${lastInitial}.`
    }
    return fullName
  }

  function ReviewTextWithTooltip({ review }: { review: string }) {
    const [expanded, setExpanded] = useState<boolean>(false)

    const isLongReview = review?.length > 200

    const displayText = expanded
      ? review
      : isLongReview
        ? review.slice(0, 200) + '...'
        : review

    return (
      <span className='relative inline-block'>
        <span
          className='font-normal text-[13px] leading-[187%] tracking-normal text-[#191919] text-justify mt-1 max-lg:text-xs'
        >
          {displayText}
          {isLongReview && (
            <span
              onClick={() => setExpanded(!expanded)}
              className='text-[#191919] font-semibold hover:text-gray-600 transition-colors text-[13px] ml-1 cursor-pointer'
            >
              {expanded ? ' show less' : ' show more'}
            </span>
          )}
        </span>
      </span>
    )
  }

  return (
    <div
      className='rounded-lg p-1 md:p-0 w-[815px] min-w-[250px] max-w-full md:mx-[2px] z-[50]'
      data-review-source={source || undefined}
    >
      {/* Top Row: Avatar + Name & Rating */}
      <div className='flex gap-4 mb-3'>
        {/* Avatar */}
        {avatarUrl ? (
          <Image
            src={avatarUrl}
            alt={name}
            className='w-12 h-12 rounded-full object-cover flex-shrink-0'
          />
        ) : (
          <div className='w-12 h-12 rounded-full bg-gray-800 flex items-center justify-center text-white font-semibold text-lg flex-shrink-0'>
            {name.charAt(0)}
          </div>
        )}

        {/* Name + Rating */}
        {/* <div className='flex-1 flex flex-col sm:flex-row sm:items-center sm:justify-between'> */}
        <div className='md:flex-1 flex flex-row items-center justify-between gap-[30px] md:gap-0'>
          <div>
            <p className='text-[14px] font-medium text-[#1E1F24] leading-[160%] tracking-normal'>
              {shortenName(name)}
            </p>
            <p className='font-normal text-[13px] leading-[160%] tracking-[-0.02em] text-[#636574]'>
              {new Date(date).toLocaleDateString('en-GB', {
                day: 'numeric',
                month: 'long',
                year: 'numeric',
              })}
            </p>
          </div>

          {/* Star Rating */}
          <div className='flex items-start gap-3 mt-1 sm:mt-0'>
            <div className='flex items-center gap-1'>
              <StarRating
                rating={rating}
                className='h-fit mt-1 items-center'
                IconClassName='text-[#319341] text-sm align-middle'
                size={20}
              />
              <span className='font-medium text-[13px] leading-[187%] tracking-normal text-[#191919] mt-[6px]'>
                {rating}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Review Text - Starts under the avatar in a new row */}
      <div className='ml-0'>
        <ReviewTextWithTooltip review={review} />
      </div>
    </div>
  )
}
